/** Shape elements (rect, ellipse, line, arrow): rendered once into layers, outline drawn by the pen. */

import { makeCanvas } from "./canvas";
import type { ShapeProps } from "./project.gen";
import type { PlanStroke } from "./strokes";

export interface ShapeLayout {
  w: number;
  h: number;
  res: number;
  outline: OffscreenCanvas;
  fill: OffscreenCanvas | null;
  strokes: PlanStroke[];
}

/** Polylines in board units relative to the shape centre. */
export function shapePolylines(s: ShapeProps): [number, number][][] {
  const hw = s.w / 2;
  const hh = s.h / 2;
  switch (s.kind) {
    case "rect":
      return [[[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh], [-hw, -hh]]];
    case "ellipse": {
      const n = 72;
      const pts: [number, number][] = [];
      for (let i = 0; i <= n; i++) {
        // start at the top and go clockwise, like a hand-drawn circle
        const a = -Math.PI / 2 + (2 * Math.PI * i) / n;
        pts.push([hw * Math.cos(a), hh * Math.sin(a)]);
      }
      return [pts];
    }
    case "line":
    case "arrow": {
      const p = s.points.length >= 2 ? s.points : [{ x: -hw, y: 0 }, { x: hw, y: 0 }];
      const shaft: [number, number][] = p.map((q) => [q.x, q.y]);
      if (s.kind === "line") return [shaft];
      const [ax, ay] = shaft[shaft.length - 2];
      const [bx, by] = shaft[shaft.length - 1];
      const ang = Math.atan2(by - ay, bx - ax);
      const L = Math.max(18, s.strokeWidth * 4);
      const wing = (d: number): [number, number] => [bx - L * Math.cos(ang + d), by - L * Math.sin(ang + d)];
      return [shaft, [wing(0.45), [bx, by], wing(-0.45)]];
    }
  }
}

export function layoutShape(s: ShapeProps, res = 2): ShapeLayout {
  const lines = shapePolylines(s);
  const pad = s.strokeWidth * 2 + 4;
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const l of lines)
    for (const [x, y] of l) {
      x1 = Math.min(x1, x); y1 = Math.min(y1, y); x2 = Math.max(x2, x); y2 = Math.max(y2, y);
    }
  // keep the centre of the layer at the shape centre so transforms rotate about it
  const hx = Math.max(Math.abs(x1), Math.abs(x2)) + pad;
  const hy = Math.max(Math.abs(y1), Math.abs(y2)) + pad;
  const W = 2 * hx;
  const H = 2 * hy;
  const toL = (x: number, y: number): [number, number] => [(x + hx) * res, (y + hy) * res];

  const o = makeCanvas(W * res, H * res);
  o.ctx.lineCap = "round";
  o.ctx.lineJoin = "round";
  o.ctx.strokeStyle = s.stroke;
  o.ctx.lineWidth = s.strokeWidth * res;
  for (const l of lines) {
    o.ctx.beginPath();
    l.forEach(([x, y], i) => (i ? o.ctx.lineTo(...toL(x, y)) : o.ctx.moveTo(...toL(x, y))));
    o.ctx.stroke();
  }
  let fill: OffscreenCanvas | null = null;
  if (s.fill && (s.kind === "rect" || s.kind === "ellipse")) {
    const f = makeCanvas(W * res, H * res);
    f.ctx.fillStyle = s.fill;
    f.ctx.beginPath();
    lines[0].forEach(([x, y], i) => (i ? f.ctx.lineTo(...toL(x, y)) : f.ctx.moveTo(...toL(x, y))));
    f.ctx.fill();
    // the outline sits on top of the fill: cut it out of the fill layer so the layers don't overlap
    f.ctx.globalCompositeOperation = "destination-out";
    f.ctx.drawImage(o.canvas, 0, 0);
    fill = f.canvas;
  }
  const r = (s.strokeWidth / 2 + 1.5) * res;
  const strokes: PlanStroke[] = lines.map((l) => ({ p: l.flatMap(([x, y]) => toL(x, y)), r }));
  return { w: o.canvas.width, h: o.canvas.height, res, outline: o.canvas, fill, strokes };
}
